const DEFAULT_TIMEOUT_MS = 120000;

const cleanText = (value) => (value == null ? "" : String(value).trim());
const cleanNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const normalized = String(value).replace(/[^0-9,.-]/g, "").replace(/,/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const normalizeOCRText = (value = "") => {
  let text = cleanText(value).replace(/\u00a0/g, " ");
  text = text.replace(/\r\n?/g, "\n");
  text = text.replace(/\s+/g, " ");
  // Remover "Sin IGV" primero
  text = text.replace(/\(Sin\s+I[.\s]*G[.\s]*V[.\s]*\)/gi, "");
  text = text.replace(/\bSin\s+I[.\s]*G[.\s]*V[.\s]*\b/gi, "");
  
  text = text.replace(/\bR[.\s]*U[.\s]*C\b/gi, "RUC");
  text = text.replace(/\bI[.\s]*G[.\s]*V\b/gi, "IGV");
  text = text.replace(/\bSub\s+Total\b/gi, "SUBTOTAL");
  text = text.replace(/\bSubTotal\b/gi, "SUBTOTAL");
  text = text.replace(/\bI\.G\.V\b/gi, "IGV");
  text = text.replace(/\bRecibo\s+No\.?\b/gi, "RECIBO");
  text = text.replace(/\s+/g, " ").trim();
  return text;
};

export const normalizeDate = (value) => {
  const text = cleanText(value).replace(/\s+/g, " ").trim();
  if (!text) return "";

  const monthMap = {
    ene: 1,
    enero: 1,
    jan: 1,
    january: 1,
    feb: 2,
    febrero: 2,
    febr: 2,
    mar: 3,
    marzo: 3,
    marz: 3,
    apr: 4,
    abril: 4,
    aprl: 4,
    may: 5,
    mayo: 5,
    jun: 6,
    junio: 6,
    jul: 7,
    julio: 7,
    aug: 8,
    agosto: 8,
    ago: 8,
    sep: 9,
    septiembre: 9,
    sept: 9,
    set: 9,
    oct: 10,
    octubre: 10,
    nov: 11,
    noviembre: 11,
    dec: 12,
    diciembre: 12,
    dic: 12,
  };

  const normalizeMonth = (monthToken) => monthMap[String(monthToken).toLowerCase()] || null;

  const compactMatch = text.match(/^(\d{8})$/);
  if (compactMatch) {
    return `${compactMatch[1].slice(0, 4)}-${compactMatch[1].slice(4, 6)}-${compactMatch[1].slice(6, 8)}`;
  }

  const ymdMatch = text.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (ymdMatch) {
    return `${ymdMatch[1]}-${String(ymdMatch[2]).padStart(2, "0")}-${String(ymdMatch[3]).padStart(2, "0")}`;
  }

  const dmyMatch = text.match(/^(\d{1,2})[-/](\d{1,2}|[A-Za-z]{3,9})[-/](\d{4})$/);
  if (dmyMatch) {
    const isNumericMonth = /^\d{1,2}$/.test(dmyMatch[2]);
    const month = isNumericMonth ? Number(dmyMatch[2]) : normalizeMonth(dmyMatch[2]);
    if (month) {
      return `${dmyMatch[3]}-${String(month).padStart(2, "0")}-${String(dmyMatch[1]).padStart(2, "0")}`;
    }
  }

  const spacedMatch = text.match(/^(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{4})$/);
  if (spacedMatch) {
    const month = normalizeMonth(spacedMatch[2]);
    if (month) {
      return `${spacedMatch[3]}-${String(month).padStart(2, "0")}-${String(spacedMatch[1]).padStart(2, "0")}`;
    }
  }

  return "";
};

const normalizeRuc = (value) => cleanText(value).replace(/[^0-9]/g, "");
const normalizeAmount = (value) => cleanNumber(value);

const extractAmountAfterLabel = (text, labelPattern, { windowChars = 50 } = {}) => {
  const scopedPattern = new RegExp(labelPattern.source, `${labelPattern.flags.includes("g") ? labelPattern.flags : `${labelPattern.flags}g`}`);
  const matches = [...text.matchAll(scopedPattern)];
  if (matches.length === 0) return null;

  const selectedMatch = matches[0];
  const startIndex = (selectedMatch.index ?? 0) + selectedMatch[0].length;
  const remainder = text.slice(startIndex, startIndex + windowChars);

  const cleanRemainder = remainder.replace(/\d+(?:\.\d+)?\s*%/g, "");
  const amountMatches = [...cleanRemainder.matchAll(/(?:\b|S\/\.?|\$)\s*(\d{1,3}(?:[.,]\d{3})*(?:[.,]\d{1,2})?|\d+(?:[.,]\d{1,2})?)/gi)];
  for (const match of amountMatches) {
    let numStr = match[1];
    if (!numStr) continue;

    if (/^(19|20)\d{2}$/.test(numStr) || /^\d{8,11}$/.test(numStr)) continue;

    if (numStr.includes(',') && numStr.includes('.')) {
      if (numStr.lastIndexOf('.') > numStr.lastIndexOf(',')) {
        numStr = numStr.replace(/,/g, '');
      } else {
        numStr = numStr.replace(/\./g, '').replace(',', '.');
      }
    } else if (numStr.includes(',')) {
      if (numStr.match(/,\d{2}$/)) {
        numStr = numStr.replace(',', '.');
      } else {
        numStr = numStr.replace(/,/g, '');
      }
    }

    const parsed = parseFloat(numStr);
    if (!isNaN(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return null;
};

const findDateInText = (text) => {
  const candidates = [];
  const patterns = [
    /(?:fecha|emitido|emision|emisión)\s*(?:el)?\s*[:\-]?\s*(\d{4}[-/]\d{1,2}[-/]\d{1,2}|\d{1,2}[-/]\d{1,2}[-/]\d{4}|\d{1,2}[-/][A-Za-z]{3,9}[-/]\d{4}|\d{1,2}\s+[A-Za-z]{3,9}\s+\d{4}|\d{8})/gi,
  ];

  for (const pattern of patterns) {
    for (const match of text.matchAll(pattern)) {
      candidates.push(match[1]);
    }
  }

  if (candidates.length === 0) {
    for (const match of text.matchAll(/(\d{4}[-/]\d{1,2}[-/]\d{1,2}|\d{1,2}[-/]\d{1,2}[-/]\d{4}|\d{1,2}[-/][A-Za-z]{3,9}[-/]\d{4}|\d{1,2}\s+[A-Za-z]{3,9}\s+\d{4}|\d{8})/gi)) {
      const value = String(match[1] || "");
      const digitsOnly = value.replace(/\D/g, "");
      if (!/^\d{11}$/.test(digitsOnly)) {
        candidates.push(value);
      }
    }
  }

  return candidates[0] || "";
};

const findDueDateInText = (text) => {
  const patterns = [
    /(?:fecha\s+de\s+vencimiento|f\.?\s*vencimiento|vencimiento|vence|pagar\s+hasta|ultimo\s+dia\s+de\s+pago)\s*[:\-]?\s*(\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}|\d{4}[-/.]\d{1,2}[-/.]\d{1,2}|\d{1,2}\s+de\s+[A-Za-z]{3,9}\s+de[l]?\s+\d{4})/gi,
  ];

  for (const pattern of patterns) {
    for (const match of text.matchAll(pattern)) {
      if (match[1]) return match[1];
    }
  }
  return "";
};

const findDocumentType = (text) => {
  if (/\bRECIBO\b/i.test(text)) return "RECIBO";
  if (/\bBOLETA\b/i.test(text)) return "BOLETA";
  if (/\bFACTURA\b/i.test(text)) return "FACTURA";
  return "FACTURA";
};

const findSeriesAndNumber = (text) => {
  const directMatch = text.match(/\b(?:RECIBO(?:\s+NO\.?\s*)?|FACTURA|BOLETA|COMPROBANTE)\s*([A-Z]{1,6}\d{1,4})[-\s]*(\d{3,20})\b/i);
  if (directMatch) {
    return { serie: directMatch[1].toUpperCase(), numero: directMatch[2] };
  }

  const fallbackMatch = text.match(/\b([A-Z]{1,6}\d{1,4})[-\s](\d{3,20})\b/i);
  if (fallbackMatch) {
    return { serie: fallbackMatch[1].toUpperCase(), numero: fallbackMatch[2] };
  }

  return { serie: "", numero: "" };
};

const RUC_CATALOG = {
  "20467534026": "America Móvil Perú S.A.C.",
  "20609212102": "MI BOLETA S.A.C.",
  "20100017491": "TELEFONICA DEL PERU S.A.A.",
  "20543254418": "ENTEL PERU S.A.",
  "20100047722": "LUZ DEL SUR S.A.A.",
  "20100128292": "ENEL DISTRIBUCION PERU S.A.A.",
  "20100053455": "SEDAPAL S.A.",
  "20100130947": "COMPAÑIA PERUANA DE RADIODIFUSION S.A.",
  "20100047218": "PETROLEOS DEL PERU PETROPERU S.A.",
  "20100128021": "REPSOL COMERCIAL SAC"
};

const cleanLegalEntityName = (candidate) => {
  let name = candidate.trim().replace(/\s+/g, " ");
  // Limpiar frases introductorias comunes antes de la Razón Social
  name = name.replace(/^.*?\b(?:para|ti|en|de|con|por|pagar|canales|canales de pago|atencion|estimado|cliente|servicio|bienvenido|empresa|razon social|nombre|somos)\b\s*/i, "");
  return name.trim();
};

const findContextName = (text, rucValue) => {
  if (rucValue && RUC_CATALOG[rucValue]) {
    return RUC_CATALOG[rucValue];
  }

  // Solo usamos la regex ciega si el RUC consultado no está en el catálogo
  if (rucValue && RUC_CATALOG[rucValue]) {
    return RUC_CATALOG[rucValue];
  }

  // Buscar nombres de empresas con sufijos de sociedad peruana (S.A.C., S.A.A., S.A., S.R.L., E.I.R.L., etc.)
  const legalEntityRegex = /(?:^|\n|[\s,])([A-ZÁÉÍÓÚÑ0-9\s.&/()'’-]{3,60}\s+(?:S\.?\s*A\.?\s*C\.?|S\.?\s*A\.?\s*A\.?|S\.?\s*A\.?|S\.?\s*R\.?\s*L\.?|E\.?\s*I\.?\s*R\.?\s*L\.?))\b/gi;
  const matches = [...text.matchAll(legalEntityRegex)];
  
  if (matches.length > 0) {
    for (const m of matches) {
      const cleaned = cleanLegalEntityName(m[1]);
      if (cleaned.length >= 4 && !/^(CLIENTE|COMPRADOR|SEÑOR|USUARIO)/i.test(cleaned)) {
        return cleaned;
      }
    }
  }

  const pattern = new RegExp(`(?:RUC|R\\.U\\.C\\.)\\s*[:.-]*\\s*${rucValue}`, "gi");
  const match = pattern.exec(text);
  if (!match) return "";

  const beforeText = cleanText(text.slice(Math.max(0, match.index - 80), match.index));
  const tokens = beforeText
    .replace(/^(nombre|razon social|cliente|comprador|empresa)\s*[:\-]*/i, "")
    .replace(/\b(?:sub(?:\s+total)?|base\s+imponible|total(?:\s+recibo)?|igv|recibo|emitido|el|no|s\/|pagar|periodo|servicio|pagos?|monto|importe|pen|usd|sin|factura|boleta)\b/gi, " ")
    .replace(/\b\d+(?:[.,]\d{1,2})?\b/g, " ")
    .replace(/[^A-Za-zÁÉÍÓÚÑáéíóúñ0-9\s.&/()'’,-]/g, " ")
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean)
    .filter((token) => !/^[.,:/()'-]+$/.test(token))
  const candidate = tokens.slice(-6).join(" ").trim();
  return candidate.length >= 2 ? candidate : "";
};

const extractInvoiceItems = (text = "", defaultSubtotal = 0) => {
  const items = [];
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);

  const sunatItemPattern = /^(?:([A-Z]{2,4})\s+)?(\d+(?:[.,]\d+)?)\s+([A-Za-zÁÉÍÓÚÑáéíóúñ0-9\s.,&/()'’.:-]{4,120}?)\s+(?:(\d+(?:[.,]\d{1,2})?)\s+)?(?:(\d+(?:[.,]\d{1,2})?)\s+)?(\d+(?:[.,]\d{1,2})?)$/i;

  for (const line of lines) {
    if (/^(SUBTOTAL|TOTAL|IGV|BASE|IMPORTE|CANTIDAD|DESCRIPCION|VALOR|UNIDAD|ITEM)/i.test(line)) continue;

    const cleanLine = line.replace(/\bObservaciones\b.*$/i, "").trim();
    const match = cleanLine.match(sunatItemPattern);
    if (match) {
      const qty = parseFloat(match[2].replace(",", ".")) || 1;
      let desc = match[3].trim().replace(/\s+/g, " ");
      const valUnit = match[4] ? parseFloat(match[4].replace(",", ".")) : null;
      const descuento = match[5] ? parseFloat(match[5].replace(",", ".")) : 0;
      const valTotal = parseFloat(match[6].replace(",", ".")) || defaultSubtotal;

      desc = desc.replace(/\s+\d+[.,]\d{2}.*$/, "").trim();

      if (desc.length >= 3 && !/^(SUBTOTAL|TOTAL|IGV|BASE|IMPORTE|SON)/i.test(desc)) {
        const precioUnitario = valUnit !== null ? valUnit : (qty > 0 ? Number((valTotal / qty).toFixed(2)) : valTotal);
        items.push({
          descripcion: desc,
          cantidad: qty,
          precioUnitario,
          descuento,
          total: valTotal,
        });
      }
    }
  }

  if (items.length === 0) {
    let mainDesc = "";

    const conceptMatch = text.match(/(?:CONCEPTO|DESCRIPCION|DETALLE|SERVICIOS?|POR)\s*[:.-]?\s*([A-Za-z0-9ÁÉÍÓÚÑáéíóúñ\s.,&/-]{4,100})/i);
    if (conceptMatch) {
      mainDesc = conceptMatch[1].split(/\b(?:SUBTOTAL|TOTAL|IGV|BASE|IMPORTE|CANTIDAD|SON|OBSERVACIONES)\b/i)[0].trim().replace(/\s+/g, " ");
    } else {
      for (const line of lines) {
        if (!/^(RUC|FACTURA|BOLETA|FECHA|SERIE|NUMERO|FORMA|SUBTOTAL|TOTAL|IGV|MONEDA|SOLES|USD|RECIBO|CLIENTE|EMPRESA)\b/i.test(line)) {
          if (line.length >= 8 && !/^\d+$/.test(line) && !/^\d{1,2}[-/]\d{1,2}[-/]\d{2,4}$/.test(line)) {
            mainDesc = line;
            break;
          }
        }
      }
    }

    if (!mainDesc || mainDesc.length < 3) {
      mainDesc = "Servicios / Productos según factura";
    }

    items.push({
      descripcion: mainDesc,
      cantidad: 1,
      precioUnitario: defaultSubtotal,
      total: defaultSubtotal,
    });
  }

  return items;
};

export const parseStructuredInvoice = (text = "") => {
  const normalizedText = normalizeOCRText(text);
  const rucMatches = [...normalizedText.matchAll(/RUC\s*[:.-]*\s*(\d{11})/gi)];
  const providerOccurrence = rucMatches.find((match) => {
    const prefix = normalizedText.slice(0, match.index).split(/\s+/).slice(-3).join(" ");
    return !/^(nombre|cliente|comprador|empresa)$/i.test(prefix.replace(/[:.-]+$/, "")) && !/\b(?:total|subtotal|igv|recibo|s\/|pagar|periodo|servicio|pagos?|monto|importe)\b/i.test(prefix);
  }) || rucMatches[0];

  const providerRuc = providerOccurrence?.[1] || "";
  const providerName = findContextName(normalizedText, providerRuc) || "";
  const clientOccurrence = rucMatches.find((match) => match[1] !== providerRuc);
  const clienteRuc = clientOccurrence?.[1] || "";
  const clienteName = findContextName(normalizedText, clienteRuc) || "";

  const { serie, numero } = findSeriesAndNumber(normalizedText);
  const fechaValue = findDateInText(normalizedText);
  const fechaVencimientoRaw = findDueDateInText(normalizedText);
  let fechaVencimiento = normalizeDate(fechaVencimientoRaw);

  const fechaEmisionNormalized = normalizeDate(fechaValue);
  if (!fechaVencimiento && fechaEmisionNormalized) {
    try {
      const d = new Date(fechaEmisionNormalized);
      if (!isNaN(d.getTime())) {
        d.setDate(d.getDate() + 30);
        fechaVencimiento = d.toISOString().split("T")[0];
      }
    } catch (e) {}
  }

  const totalMatch = extractAmountAfterLabel(normalizedText, /(?:^|[^A-Za-z])(?:TOTAL(?:\s+RECIBO)?|IMPORTE\s+TOTAL|MONTO\s+TOTAL|TOTAL\s+A\s+PAGAR)/i);
  const subtotalMatch = extractAmountAfterLabel(normalizedText, /(?:^|[^A-Za-z])(?:SUBTOTAL|BASE\s+IMPONIBLE)/i);
  const igvMatch = extractAmountAfterLabel(normalizedText, /(?:^|[^A-Za-z])(?:IGV|I\.G\.V\.)/i);
  const moneda = normalizedText.includes("US$") || normalizedText.includes("USD") ? "USD" : "PEN";

  let total = totalMatch ?? 0;
  let igv = igvMatch ?? 0;
  let subtotal = subtotalMatch;

  // Si igv se extrajo como 18 (por el literal del 18%) o como 0.18, lo descartamos como monto real
  if (igv === 18 || igv === 0.18) {
    igv = 0;
  }

  if (subtotal === null || subtotal === undefined) {
    if (total > 0) {
      if (igv > 0 && total >= igv) {
        subtotal = Number((total - igv).toFixed(2));
      } else {
        subtotal = Number((total / 1.18).toFixed(2));
        igv = Number((total - subtotal).toFixed(2));
      }
    } else {
      subtotal = 0;
    }
  } else {
    // Si subtotal es un número válido, y total > 0 e igv es 0 o inválido
    if (total > subtotal && (igv === 0 || igv === null || igv === undefined)) {
      igv = Number((total - subtotal).toFixed(2));
    }
  }

  if (total === 0 && subtotal > 0) {
    if (igv === 0) igv = Number((subtotal * 0.18).toFixed(2));
    total = Number((subtotal + igv).toFixed(2));
  }

  const items = extractInvoiceItems(normalizedText, subtotal);

  if (items.length > 0) {
    const sumItemsTotal = items.reduce((acc, it) => acc + (it.total || 0), 0);
    if (sumItemsTotal > 0 && (subtotal === 0 || total === 0)) {
      subtotal = Number(sumItemsTotal.toFixed(2));
      if (igv === 0) igv = Number((subtotal * 0.18).toFixed(2));
      if (total === 0) total = Number((subtotal + igv).toFixed(2));
    }
  }

  return {
    proveedor: {
      ruc: normalizeRuc(providerRuc),
      razonSocial: providerName,
      direccion: "",
    },
    comprobante: {
      tipo: findDocumentType(normalizedText),
      serie,
      numero,
      fechaEmision: fechaEmisionNormalized,
      fechaVencimiento: fechaVencimiento || "",
      moneda,
    },
    montos: {
      subtotal,
      igv,
      total,
      descuentos: 0,
      otrosCargos: 0,
    },
    items,
    cliente: {
      ruc: normalizeRuc(clienteRuc),
      razonSocial: clienteName,
    },
    metadatos: {
      fuente: "GOOGLE_VISION",
      confianza: 70,
      fechaProcesamiento: new Date().toISOString(),
    },
  };
};

export const parseGoogleVisionText = (text = "") => parseStructuredInvoice(text);

export class GoogleVisionProvider {
  constructor({ timeoutMs = DEFAULT_TIMEOUT_MS } = {}) {
    this.timeoutMs = timeoutMs;
  }

  async extractStructuredData({ text = "" } = {}) {
    if (!text) {
      throw new Error("Google Vision no pudo procesar el documento (sin texto OCR)");
    }

    return parseGoogleVisionText(text);
  }
}

export default GoogleVisionProvider;
